import { useRef, useState } from "react";
import { AccountUsage } from "../usage/account-usage";
import { CredentialOperation } from "./credential-operation";
import { CredentialTable } from "./credential-table";
import {
  type CredentialIntent,
  type CredentialOpening,
} from "./credential-intent";

type ProfileCredentialsProps = {
  /** Every player on the account, in the API's order. */
  agents: { id: string; name: string }[];
};

const sectionLabel =
  "font-plex-sans text-[16px] leading-[20.8px] font-semibold text-[#161616]";
const bodyText = "font-plex-sans text-[16px] leading-[24px] text-[#5e5e5e]";

export function ProfileCredentials({ agents }: ProfileCredentialsProps) {
  return (
    <div className="flex flex-col gap-[48px] pb-[48px]">
      <section
        aria-labelledby="account-credits"
        className="flex flex-col gap-[24px] border-t border-[rgba(22,22,22,0.13)] pt-[24px]"
      >
        <h2 id="account-credits" className={sectionLabel}>
          Experience credits
        </h2>
        <AccountUsage />
      </section>
      <section
        aria-labelledby="account-tokens"
        className="flex flex-col gap-[24px] border-t border-[rgba(22,22,22,0.13)] pt-[24px]"
      >
        <h2 id="account-tokens" className={sectionLabel}>
          Access tokens
        </h2>
        <Credentials agents={agents} />
      </section>
    </div>
  );
}

function Credentials({
  agents,
}: {
  agents: ProfileCredentialsProps["agents"];
}) {
  const [intent, setIntent] = useState<CredentialIntent | null>(null);
  const finalFocusRef = useRef<HTMLElement | null>(null);
  const onOpen = ({ intent: next, invoker }: CredentialOpening) => {
    finalFocusRef.current =
      next.action === "create" ? next.successFocusRef.current : invoker;
    setIntent(next);
  };
  return (
    <div className="flex flex-col gap-[48px]">
      <CredentialTable
        headingLevel={2}
        summary
        target={{ kind: "user" }}
        title="Your account tokens"
        description="Tokens let the Softmax CLI or your coding agent act as you. Mint one, then paste it into the tool."
        onOpen={onOpen}
      />
      {agents.length === 0 ? (
        <p className={bodyText}>
          No agents yet. Name one on the account page first.
        </p>
      ) : (
        agents.map((agent) => (
          <CredentialTable
            key={agent.id}
            headingLevel={2}
            summary
            target={{
              kind: "player",
              playerId: agent.id,
              playerName: agent.name,
            }}
            title={`Agent credentials · ${agent.name}`}
            description="Tokens that act only as this agent."
            onOpen={onOpen}
          />
        ))
      )}
      {intent ? (
        <CredentialOperation
          intent={intent}
          finalFocusRef={finalFocusRef}
          onClose={() => setIntent(null)}
        />
      ) : null}
    </div>
  );
}
